// src/components/ErrorMessage/ErrorMessage.tsx

import React from 'react';
import {
    X,
    WifiOff,
    Clock,
    FileWarning,
    AlertTriangle,
    ServerCrash,
    LucideIcon
} from 'lucide-react';
import { cn } from '../../lib/utils';

export type ErrorType = 'file' | 'network' | 'server' | 'timeout' | 'unknown';

interface ErrorMessageProps {
    type?: ErrorType;
    message: string;
    details?: string;
    onClose?: () => void;
}

/**
 * Type-variant classes map (light + dark mode)
 * Mirrors the original CSS: .error-type-{type} + .dark-mode .error-type-{type}
 */
const errorTypeClasses: Record<ErrorType, string> = {
    // .error-type-network: bg-[#fee2e2] border-[#fecaca] text-[#dc2626] | dark: bg-[rgba(220,38,38,0.1)] border-[rgba(220,38,38,0.2)] text-[#ef4444]
    network: 'bg-[#fee2e2] border-[#fecaca] text-[#dc2626] dark:bg-[rgba(220,38,38,0.1)] dark:border-[rgba(220,38,38,0.2)] dark:text-[#ef4444]',
    // .error-type-validation: bg-[#fff7ed] border-[#fed7aa] text-[#ea580c] | dark: bg-[rgba(234,88,12,0.1)] border-[rgba(234,88,12,0.2)] text-[#fb923c]
    // NOTE: 'timeout' uses the validation styles as there was no 'timeout' type in CSS (defaults)
    timeout: 'bg-[#fff7ed] border-[#fed7aa] text-[#ea580c] dark:bg-[rgba(234,88,12,0.1)] dark:border-[rgba(234,88,12,0.2)] dark:text-[#fb923c]',
    // .error-type-server: bg-[#eff6ff] border-[#bfdbfe] text-[#2563eb] | dark: bg-[rgba(37,99,235,0.1)] border-[rgba(37,99,235,0.2)] text-[#60a5fa]
    server: 'bg-[#eff6ff] border-[#bfdbfe] text-[#2563eb] dark:bg-[rgba(37,99,235,0.1)] dark:border-[rgba(37,99,235,0.2)] dark:text-[#60a5fa]',
    // .error-type-file: bg-[#f0fdf4] border-[#bbf7d0] text-[#16a34a] | dark: bg-[rgba(22,163,74,0.1)] border-[rgba(22,163,74,0.2)] text-[#4ade80]
    file: 'bg-[#f0fdf4] border-[#bbf7d0] text-[#16a34a] dark:bg-[rgba(22,163,74,0.1)] dark:border-[rgba(22,163,74,0.2)] dark:text-[#4ade80]',
    // .error-type-unknown: bg-[#f3f4f6] border-[#e5e7eb] text-[#4b5563] | dark: bg-[rgba(75,85,99,0.1)] border-[rgba(75,85,99,0.2)] text-[#9ca3af]
    unknown: 'bg-[#f3f4f6] border-[#e5e7eb] text-[#4b5563] dark:bg-[rgba(75,85,99,0.1)] dark:border-[rgba(75,85,99,0.2)] dark:text-[#9ca3af]',
};

const ErrorMessage: React.FC<ErrorMessageProps> = ({
    type = 'unknown',
    message,
    details,
    onClose
}) => {
    const getIcon = (): LucideIcon => {
        switch (type) {
            case 'file':
                return FileWarning;
            case 'network':
                return WifiOff;
            case 'server':
                return ServerCrash;
            case 'timeout':
                return Clock;
            default:
                return AlertTriangle;
        }
    };

    const IconComponent = getIcon();

    return (
        /*
         * .error-message-container removed (it was just margin wrapper, kept as plain div wrapper if needed)
         * .error-container:
         *   m-0 p-3 rounded-lg flex items-start justify-between
         *   animate-[slideIn_0.3s_ease-out] border border-transparent
         *   + type variant classes (bg + border-color + text-color)
         * .dark-mode .error-container: bg + border overrides via type variant dark: classes
         */
        <div
            className={cn(
                'mb-4',  // .error-message-container: margin: 0 0 1rem 0
            )}
        >
            <div
                className={cn(
                    // .error-container base
                    'm-0 p-3 rounded-lg flex items-start justify-between',
                    'animate-[slideIn_0.3s_ease-out]',
                    'border border-transparent',
                    // type-specific colors (light + dark)
                    errorTypeClasses[type] ?? errorTypeClasses.unknown,
                )}
            >
                {/* .error-content: flex items-start gap-2 flex-1 */}
                <div className="flex items-start gap-2 flex-1">
                    {/* .error-icon: shrink-0 w-4 h-4 (currentColor from parent) */}
                    <IconComponent size={20} className="shrink-0 w-4 h-4" />

                    {/* .error-text: flex flex-col gap-0.5 text-sm */}
                    <div className="flex flex-col gap-0.5 text-sm">
                        {/* .error-message: font-medium (color inherited from container) */}
                        <span className="font-medium">{message}</span>

                        {/* .error-details: text-[0.8125rem] opacity-85 (color inherited) */}
                        {details && (
                            <span className="text-[0.8125rem] opacity-85">{details}</span>
                        )}
                    </div>
                </div>

                {/* .error-close:
                    bg-transparent border-0 p-1 -m-1 ml-2 rounded cursor-pointer opacity-70 transition-all duration-200 ease
                    hover:opacity-100 hover:bg-[rgba(220,38,38,0.1)] dark:hover:bg-[rgba(239,68,68,0.1)]
                    Color is inherited from parent (type-variant text color) */}
                {onClose && (
                    <button
                        className="bg-transparent border-0 p-1 -my-1 -mr-1 ml-2 rounded cursor-pointer opacity-70 transition-all duration-200 ease-[ease] hover:opacity-100 hover:bg-[rgba(220,38,38,0.1)] dark:hover:bg-[rgba(239,68,68,0.1)]"
                        onClick={onClose}
                        aria-label="Close error message"
                    >
                        <X size={16} />
                    </button>
                )}
            </div>
        </div>
    );
};

export default ErrorMessage;
